import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { Input } from "@/components/ui/baseUi/input"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BoldIcon } from "@images/richText/boldIcon"
import { BulletListIcon } from "@images/richText/bulletListIcon"
import { ItalicIcon } from "@images/richText/italicIcon"
import { LinkIcon } from "@images/richText/linkIcon"
import { OrderedListIcon } from "@images/richText/orderedListIcon"
import { UnderlineIcon } from "@images/richText/underlineIcon"
import { word } from "@modules/core/utils/i18n"
import { Editor } from "@tiptap/react"
import { useMemo, useState } from "react"

export function RichTextMenuBar({
  editor,
  className,
  ...props
}: {
  editor: Editor | null
  className?: string
} & React.ComponentProps<"div">) {
  const { theme } = useTheme()
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")

  const isLinkButtonDisabled = useMemo(() => {
    if (!editor) return true

    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to, " ")
    return !(to > from && selectedText.trim().length > 0)
  }, [editor?.state.selection, editor?.state.doc])

  if (!editor) {
    return null
  }

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const setLink = () => {
    // empty
    if (linkUrl === "" || linkUrl === undefined) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
      setShowLinkModal(false)
      setLinkUrl("")

      return
    }
    if (!isValidUrl(linkUrl)) {
      toast.error(word("global.richTextForm.linkError"), { classNames: errorToastClassName() })
      return
    }

    // update link
    try {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run()
      setShowLinkModal(false)
      setLinkUrl("")
    } catch (e: any) {
      toast.error(e.message, { classNames: errorToastClassName() })
    }
  }

  const buttonClassName = (isActive: boolean, isDisabled?: boolean) =>
    isDisabled
      ? `cursor-auto ${theme === Theme.OwnerDark ? "bg-neutral-100" : "bg-neutral-200"}`
      : `cursor-pointer ${isActive ? "bg-primary-500" : (theme === Theme.OwnerDark ? "bg-neutral-100" : "bg-neutral-200") + " hover:bg-neutral-300"}`

  return (
    <div className={cn("mt-1 mb-2 flex flex-row items-center gap-3", className)} {...props}>
      <div className="flex flex-row items-center">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`rounded-l-sm px-2 ${buttonClassName(editor.isActive("heading", { level: 1 }))}`}
          style={{ height: 32 }}
        >
          <Text fontStyle="TL">{word("global.richTextForm.blockTypes.title")}</Text>
        </button>

        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`rounded-r-sm px-2 ${buttonClassName(editor.isActive("heading", { level: 2 }))}`}
          style={{ height: 32 }}
        >
          <Text fontStyle="TS">{word("global.richTextForm.blockTypes.subtitle")}</Text>
        </button>
      </div>

      <div className="flex flex-row items-center">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`rounded-l-sm px-1 ${buttonClassName(editor.isActive("bold"))}`}
          style={{ height: 32 }}
        >
          <BoldIcon color="currentColor" className="align-middle text-textDefault-body" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`px-1 ${buttonClassName(editor.isActive("italic"))}`}
          style={{ height: 32 }}
        >
          <ItalicIcon color="currentColor" className="align-middle text-textDefault-body" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={`rounded-r-sm px-1 ${buttonClassName(editor.isActive("underline"))}`}
          style={{ height: 32 }}
        >
          <UnderlineIcon color="currentColor" className="align-middle text-textDefault-body" />
        </button>
      </div>

      <div className="flex flex-row items-center">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`rounded-l-sm px-1 ${buttonClassName(editor.isActive("bulletList"))}`}
          style={{ height: 32 }}
        >
          <BulletListIcon color="currentColor" className="align-middle text-textDefault-body" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`rounded-r-sm px-1 ${buttonClassName(editor.isActive("orderedList"))}`}
          style={{ height: 32 }}
        >
          <OrderedListIcon color="currentColor" className="align-middle text-textDefault-body" />
        </button>
      </div>

      <div className="flex flex-row items-center">
        <button
          onClick={() => {
            setLinkUrl(editor.getAttributes("link").href ?? "")
            setShowLinkModal(true)
          }}
          className={`rounded-sm px-1 ${buttonClassName(editor.isActive("link"), isLinkButtonDisabled)}`}
          style={{ height: 32 }}
          disabled={isLinkButtonDisabled}
        >
          <LinkIcon color="currentColor" className="align-middle text-textDefault-body" />
        </button>
      </div>

      <Dialog open={showLinkModal} onOpenChange={() => setShowLinkModal(false)}>
        <DialogContent className="h-57.5 w-162.5">
          <DialogHeader>
            <DialogTitle>{word("global.richTextForm.blockTypes.link")}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>

          <Input
            className="w-full"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setLinkUrl(e.target.value)
            }}
            placeholder={word("global.form.linkInputPlaceholder")}
            value={linkUrl}
          />

          <DialogFooter className="mt-4 flex flex-row justify-end gap-2">
            <Button
              onClick={() => {
                setShowLinkModal(false)
                setLinkUrl("")
              }}
              variant="outline"
            >
              {word("global.cancel")}
            </Button>

            <Button
              onClick={() => {
                setLink()
              }}
              variant="primary"
            >
              {word("global.check")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* <HUDialog
        visible={showLinkModal}
        header={word("global.richTextForm.blockTypes.link")}
        onHide={() => setShowLinkModal(false)}
        style={{ width: 650, height: 230 }}
      >
        <Input
          className="w-full"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setLinkUrl(e.target.value)
          }}
          placeholder={word("global.form.linkInputPlaceholder")}
          value={linkUrl}
        />

        <div className="mt-4 flex flex-row justify-end gap-2">
          <Button
            onClick={() => {
              setShowLinkModal(false)
              setLinkUrl("")
            }}
            variant="outline"
          >
            {word("global.cancel")}
          </Button>

          <Button
            onClick={() => {
              setLink()
            }}
            variant="primary"
          >
            {word("global.check")}
          </Button>
        </div>
      </HUDialog> */}
    </div>
  )
}
